import React, { forwardRef, memo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../../assets/contexts/ThemeContext';
import { usePageLayout } from '../../../assets/contexts/PageLayoutContext';
import MainMenu from '../../../core/menu/MainMenu';
import MegaMenu from '../../../core/menu/MegaMenu';
import TopNavDropdownMenu from '../../../core/menu/TopNavDropdownMenu';
import HeaderToolbar from './HeaderToolbar';
import { useStickyHeader } from '../../../assets/hooks/useStickyHeader';
import { resolveHeaderLogoTagText } from '../../../assets/utils/comm/headerLocalized';

/**
 * 헤더 하단 영역 컴포넌트
 * 역할: 헤더 하단 영역 (로고, 메인 메뉴, 모바일 메뉴 버튼)
 * 성능 최적화: React.memo로 메모이제이션
 */
const HeaderBottom = memo(forwardRef(({ menuData, onMenuClick, hideMenu = false, hideToolbar = false }, ref) => {
  const themeContext = useTheme();
  const themeConfig = themeContext?.themeConfig || {};
  const { i18n } = useTranslation();
  const isSticky = useStickyHeader();
  const { bannerData } = usePageLayout();

  // 배너가 없는 경우 확인 (배너가 없거나, 로딩 중이 아니면서 제목이 없는 경우)
  const hasBanner = bannerData && bannerData.title && !bannerData.loading;

  return (
    <div
      className={`header-bottom sticky-bar ${isSticky ? 'stick' : ''}`}
      style={{
        backgroundColor: themeConfig.headerBottomBackgroundColor || '#ffffff',
        color: themeConfig.headerBottomTextColor || '#000000',
        paddingTop: `${themeConfig.headerBottomHeight || 15}px`,
        paddingBottom: `${themeConfig.headerBottomHeight || 15}px`,
        ...(hasBanner ? {} : { borderBottom: '1px solid #e9ecef' })
      }}
    >
      <div className="container">
        <div className="row align-items-center">
          <div className={hideMenu ? "col-12" : "col-lg-3 col-md-6 col-9"}>
            <div className="logo">
              <Link to="/">
                {(() => {
                  const headerDataLogo = typeof window !== 'undefined' && window.headerContent?.headerData?.logo;
                  const headerLogo = headerDataLogo || themeConfig.headerLogo || { type: 'image', image: '/assets/images/logo/logo.png' };

                  // 태그 타입인 경우
                  if (headerLogo.type === 'tag' && headerLogo.tag) {
                    const tag = headerLogo.tag;
                    const logoStyle = tag.logo || {};
                    const { title: displayTitle, subtitle: displaySubtitle } = resolveHeaderLogoTagText(
                      tag,
                      i18n.language
                    );

                    return (
                      <div className="header-logo-tag-container">
                        <div
                          className="header-logo-tag-circle"
                          style={{
                            width: `${logoStyle.size || 49}px`,
                            height: `${logoStyle.size || 49}px`,
                            // background: logoStyle.backgroundColor || 'linear-gradient(135deg, #9333ea 0%, #3b82f6 100%)',
                            background: '#01284a',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0
                          }}
                        >
                          <span
                            className="header-logo-tag-letter"
                            style={{
                              fontSize: logoStyle.fontSize || '1.5rem',
                              fontWeight: 'bold',
                              color: logoStyle.color || '#ffffff'
                            }}
                          >
                            {logoStyle.letter || 'G'}
                          </span>
                        </div>
                        <div className="header-logo-tag-text-container">
                          {(displayTitle != null && displayTitle !== '') && (
                            <h3
                              className="header-logo-tag-title"
                              style={{
                                color: tag.titleColor || '#ffffff',
                                fontSize: tag.titleStyle?.fontSize || '20px',
                                fontWeight: tag.titleStyle?.fontWeight || 700,
                                lineHeight: tag.titleStyle?.lineHeight || 1.2,
                                letterSpacing: tag.titleStyle?.letterSpacing || '0',
                                textTransform: tag.titleStyle?.textTransform || 'none',
                                margin: '0px'
                              }}
                            >
                              {displayTitle}
                            </h3>
                          )}
                          {(displaySubtitle != null && displaySubtitle !== '') && (
                            <p
                              className="header-logo-tag-subtitle"
                              style={{
                                color: tag.subtitleColor || '#999999',
                                fontSize: tag.subtitleStyle?.fontSize || '14px',
                                fontWeight: tag.subtitleStyle?.fontWeight || 400,
                                lineHeight: tag.subtitleStyle?.lineHeight || 1.5,
                                letterSpacing: tag.subtitleStyle?.letterSpacing || '0',
                                textTransform: tag.subtitleStyle?.textTransform || 'none',
                                margin: 0
                              }}
                            >
                              {displaySubtitle}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  }

                  // 이미지 타입인 경우 (기본)
                  return (
                    <img
                      src={headerLogo.image || '/assets/images/logo/logo.png'}
                      alt="logo"
                    />
                  );
                })()}
              </Link>
            </div>
          </div>
          {!hideMenu && (
            <div className="col-lg-9 d-none d-lg-flex justify-content-end">
              {(themeConfig.submenuType === 'dropdown')
                ? <TopNavDropdownMenu ref={ref} menuData={menuData} themeConfig={themeConfig} />
                : <MainMenu ref={ref} menuData={menuData} themeConfig={themeConfig} />
              }
            </div>
          )}
          {/* 모바일/태블릿용 햄버거 메뉴 */}
          {!hideToolbar && !hideMenu && (
            <div className="col-3 col-md-6 d-flex d-lg-none justify-content-end">
              <HeaderToolbar variant="header" onMenuClick={onMenuClick} />
            </div>
          )}
        </div>
      </div>
      {/* 메가메뉴: 상단 배치이고 하위 메뉴 타입이 메가메뉴일 때만 표시 */}
      {!hideMenu && menuData && menuData.length > 0 && themeConfig.submenuType !== 'dropdown' && (
        <MegaMenu menuData={menuData} />
      )}
    </div>
  );
}));

HeaderBottom.displayName = 'HeaderBottom';

export default HeaderBottom;

